import React, { useState } from 'react';
import { 
    Box, 
    Text, 
    Grid, 
    HStack, 
    VStack, 
    Divider, 
    Badge, 
    Button,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Icon
} from '@chakra-ui/react';
import { FaTicketAlt, FaDharmachakra, FaCoins } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';

const LotteryTicketManager = () => {
    const { gameState, allocateTicketsToWheel, convertTicketsToJinu } = useGame();
    const { isOpen: isTicketModalOpen, onOpen: onTicketModalOpen, onClose: onTicketModalClose } = useDisclosure();
    
    // Local state for ticket allocation
    const [ticketsToAllocate, setTicketsToAllocate] = useState(0);
    const [ticketsToConvert, setTicketsToConvert] = useState(0);
    
    // Destructure game state with defaults
    const { 
        ticketsCollected = 0,
        wheelTickets = 0
    } = gameState || {};
    
    // Calculate ticket stats
    const ticketStats = {
        totalTickets: ticketsCollected + wheelTickets,
        availableTickets: ticketsCollected,
        wheelTickets: wheelTickets
    };
    
    // Handle allocating tickets to wheel
    const handleAllocateTickets = () => {
        if (ticketsToAllocate > 0 && ticketsToAllocate <= ticketStats.availableTickets) {
            allocateTicketsToWheel(ticketsToAllocate);
            setTicketsToAllocate(0);
        }
    };
    
    // Handle converting tickets to JINU
    const handleConvertTickets = () => {
        if (ticketsToConvert > 0 && ticketsToConvert <= ticketStats.availableTickets) {
            convertTicketsToJinu(ticketsToConvert);
            setTicketsToConvert(0);
        }
    };
    
    return (
        <>
            {/* Ticket Counter */}
            <Box 
                p={3} 
                bg="purple.800" 
                borderRadius="md" 
                mb={4}
            >
                <HStack justify="space-between" mb={2}>
                    <HStack>
                        <Text fontSize="xl">🎟️</Text>
                        <Text fontWeight="bold">Lottery Tickets</Text>
                    </HStack>
                    <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                        {ticketStats.totalTickets}
                    </Badge>
                </HStack>
                
                <Divider my={2} />
                
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                    <Box p={2} bg="whiteAlpha.100" borderRadius="md">
                        <Text fontSize="sm" color="gray.300">Available</Text>
                        <Text fontWeight="bold" color="green.300">{ticketStats.availableTickets}</Text>
                    </Box>
                    
                    <Box p={2} bg="whiteAlpha.100" borderRadius="md">
                        <Text fontSize="sm" color="gray.300">On Wheel</Text>
                        <Text fontWeight="bold" color="yellow.300">{ticketStats.wheelTickets}</Text>
                    </Box>
                </Grid>
                
                <Button 
                    size="sm" 
                    colorScheme="purple" 
                    w="100%" 
                    mt={3}
                    onClick={onTicketModalOpen}
                    leftIcon={<FaDharmachakra />}
                >
                    Manage Tickets
                </Button>
            </Box>
            
            {/* Ticket Management Modal */}
            <Modal isOpen={isTicketModalOpen} onClose={onTicketModalClose} size="md">
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent bg="gray.800" color="white">
                    <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
                        <HStack>
                            <Icon as={FaTicketAlt} color="green.300" />
                            <Text>Manage Lottery Tickets</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    
                    <ModalBody py={4}>
                        <VStack spacing={5} align="stretch">
                            {/* Ticket Stats */}
                            <Box p={3} bg="whiteAlpha.100" borderRadius="md">
                                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                    <Box>
                                        <Text fontSize="sm" color="gray.400">Available Tickets</Text>
                                        <Text fontSize="xl" fontWeight="bold" color="green.300">
                                            {ticketStats.availableTickets}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="sm" color="gray.400">Wheel Tickets</Text>
                                        <Text fontSize="xl" fontWeight="bold" color="yellow.300">
                                            {ticketStats.wheelTickets}
                                        </Text>
                                    </Box>
                                </Grid>
                            </Box>
                            
                            {/* Allocate to Wheel */}
                            <Box p={3} bg="purple.900" borderRadius="md">
                                <HStack mb={2}>
                                    <Icon as={FaDharmachakra} color="yellow.300" />
                                    <Text fontWeight="bold">Allocate to Wheel of Fortune</Text>
                                </HStack>
                                
                                <Text fontSize="sm" color="gray.300" mb={3}>
                                    Allocate tickets to the Wheel of Fortune to increase your chance of winning big prizes.
                                    The more tickets you allocate, the larger your share of the wheel.
                                </Text>
                                
                                <HStack mb={2}>
                                    <NumberInput 
                                        min={0} 
                                        max={ticketStats.availableTickets} 
                                        value={ticketsToAllocate}
                                        onChange={(valueString) => setTicketsToAllocate(parseInt(valueString) || 0)}
                                        flex={1}
                                    >
                                        <NumberInputField />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                    
                                    <Button 
                                        colorScheme="yellow" 
                                        onClick={handleAllocateTickets}
                                        isDisabled={ticketsToAllocate <= 0 || ticketsToAllocate > ticketStats.availableTickets}
                                    >
                                        Allocate
                                    </Button>
                                </HStack>
                                
                                <Slider
                                    min={0}
                                    max={ticketStats.availableTickets}
                                    value={ticketsToAllocate}
                                    onChange={(val) => setTicketsToAllocate(val)}
                                    mb={2}
                                >
                                    <SliderTrack bg="whiteAlpha.200">
                                        <SliderFilledTrack bg="yellow.500" />
                                    </SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </Box>
                            
                            {/* Convert to JINU */}
                            <Box p={3} bg="green.900" borderRadius="md">
                                <HStack mb={2}>
                                    <Icon as={FaCoins} color="green.300" />
                                    <Text fontWeight="bold">Convert to JINU</Text>
                                </HStack>
                                
                                <Text fontSize="sm" color="gray.300" mb={3}>
                                    Convert your tickets to JINU currency at a rate of 0.1 JINU per ticket.
                                </Text>
                                
                                <HStack mb={2}>
                                    <NumberInput 
                                        min={0} 
                                        max={ticketStats.availableTickets} 
                                        value={ticketsToConvert}
                                        onChange={(valueString) => setTicketsToConvert(parseInt(valueString) || 0)}
                                        flex={1}
                                    >
                                        <NumberInputField />
                                        <NumberInputStepper>
                                            <NumberIncrementStepper />
                                            <NumberDecrementStepper />
                                        </NumberInputStepper>
                                    </NumberInput>
                                    
                                    <Button 
                                        colorScheme="green" 
                                        onClick={handleConvertTickets}
                                        isDisabled={ticketsToConvert <= 0 || ticketsToConvert > ticketStats.availableTickets}
                                    >
                                        Convert
                                    </Button>
                                </HStack>
                                
                                <Slider
                                    min={0}
                                    max={ticketStats.availableTickets}
                                    value={ticketsToConvert}
                                    onChange={(val) => setTicketsToConvert(val)}
                                    mb={2}
                                >
                                    <SliderTrack bg="whiteAlpha.200">
                                        <SliderFilledTrack bg="green.500" />
                                    </SliderTrack>
                                    <SliderThumb />
                                </Slider>
                                
                                <Text fontSize="sm" color="green.300" textAlign="right">
                                    {(ticketsToConvert * 0.1).toFixed(1)} JINU
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>
                    
                    <ModalFooter>
                        <Button variant="ghost" onClick={onTicketModalClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default LotteryTicketManager;
